import { Button } from "@ths/ui";
import { type ReactNode, useEffect, useRef } from "react";

import type { SelfCheckinLookupReservation } from "../generated/graphql";

interface StayNavigationProps {
  reservations: SelfCheckinLookupReservation[];
  selectedAssignmentId: string | null;
  onSelect: (reservation: SelfCheckinLookupReservation) => void;
  onUpcomingStays?: () => void;
  headerAction: ReactNode;
  welcomeMessage?: string;
  disabled?: boolean;
}

export default function StayNavigation({
  reservations,
  selectedAssignmentId,
  onSelect,
  onUpcomingStays,
  headerAction,
  welcomeMessage,
  disabled = false,
}: StayNavigationProps) {
  const activeStays = reservations.filter((stay) => Boolean(stay.checkedInAt));
  const upcomingCount = reservations.length - activeStays.length;
  const selectedButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selectedButton.current?.scrollIntoView?.({
      block: "nearest",
      inline: "nearest",
    });
  }, [selectedAssignmentId, activeStays.length]);

  return (
    <nav
      aria-label="Your Basecamp stays"
      className="relative z-10 px-6 pt-4 text-on-navy"
    >
      <div className="mx-auto w-full max-w-sm space-y-3 border-b border-on-navy/15 pb-4">
        <div className="flex items-center justify-between gap-2">
          {welcomeMessage ? (
            <h1 className="min-w-0 flex-1 break-words font-oswald text-xl font-medium uppercase tracking-wide text-on-navy sm:text-2xl">
              {welcomeMessage}
            </h1>
          ) : (
            <div className="flex-1" />
          )}
          {headerAction}
        </div>
        <div className="flex min-w-0 items-center gap-2">
          {activeStays.length > 0 && (
            <ul
              aria-label="Active stays"
              className="flex min-w-0 flex-1 scroll-px-1 gap-2 overflow-x-auto overscroll-x-contain p-1"
            >
              {activeStays.map((stay) => {
                const selected = stay.assignmentId === selectedAssignmentId;
                return (
                  <li
                    key={stay.assignmentId}
                    className="flex max-w-[85%] shrink-0"
                  >
                    <Button
                      type="button"
                      ref={selected ? selectedButton : undefined}
                      variant="ghost"
                      aria-pressed={selected}
                      onClick={() => onSelect(stay)}
                      onFocus={(event) =>
                        event.currentTarget.scrollIntoView?.({
                          block: "nearest",
                          inline: "nearest",
                        })
                      }
                      disabled={disabled}
                      className={`relative h-auto min-h-11 w-full justify-start whitespace-normal break-words rounded-xl border px-4 py-3 text-left text-on-navy hover:bg-navy focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong ${
                        selected
                          ? "border-on-navy/30 bg-navy"
                          : "border-on-navy/15 bg-on-navy/5"
                      }`}
                    >
                      <span className="min-w-0 break-words">
                        {stay.basecampName}
                      </span>
                      {selected && (
                        <span
                          aria-hidden="true"
                          className="absolute inset-x-4 bottom-0 h-0.5 bg-highlight"
                        />
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          {upcomingCount > 0 && onUpcomingStays && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onUpcomingStays}
              disabled={disabled}
              className="h-11 shrink-0 px-2 text-on-navy/90 hover:bg-on-navy/10 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
            >
              Upcoming ({upcomingCount})
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
}
